import { authSelectors } from 'ducks/auth'
import { useDataProvider } from 'ra-core'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { HardwareSupplierFilterKeyType } from '../type'
import { ShopCategory } from './types'
import { useShopCategoryConfig } from './useShopCategoryConfig'

type ComponentBucketAvailability = {
  module: boolean
  inverter: boolean
  battery: boolean
  other: boolean
  otherSubtypes: Set<string>
}

const DEFAULT_BUCKET_AVAILABILITY: ComponentBucketAvailability = {
  module: false,
  inverter: false,
  battery: false,
  other: false,
  otherSubtypes: new Set(),
}

const supplierAvailabilityCache = new Map<string, ComponentBucketAvailability>()
const supplierAvailabilityInFlight = new Map<string, Promise<ComponentBucketAvailability>>()

const normalizeAvailabilityResponse = (availabilityResponse: Record<string, unknown>): ComponentBucketAvailability => {
  const otherSubtypesRaw = availabilityResponse.other_subtypes
  const otherSubtypes = Array.isArray(otherSubtypesRaw)
    ? new Set(otherSubtypesRaw.filter((subtype): subtype is string => typeof subtype === 'string'))
    : new Set<string>()

  return {
    module: Boolean(availabilityResponse.module),
    inverter: Boolean(availabilityResponse.inverter),
    battery: Boolean(availabilityResponse.battery),
    other: Boolean(availabilityResponse.other),
    otherSubtypes,
  }
}

const categoryHasOtherStock = (
  category: ShopCategory,
  bucketAvailability: ComponentBucketAvailability,
  getSubtypesForCategory: (category: ShopCategory) => string[]
): boolean => {
  if (!bucketAvailability.other) return false

  const subtypes = getSubtypesForCategory(category)
  if (subtypes.length === 0) {
    return bucketAvailability.otherSubtypes.size > 0
  }

  return subtypes.some((subtype) => bucketAvailability.otherSubtypes.has(subtype))
}

const getSupplierAvailability = (
  dataProvider: ReturnType<typeof useDataProvider>,
  orgId: number,
  distributor: HardwareSupplierFilterKeyType
): Promise<ComponentBucketAvailability> => {
  const cacheKey = `${orgId}:${distributor}`
  const cachedAvailability = supplierAvailabilityCache.get(cacheKey)
  if (cachedAvailability) {
    return Promise.resolve(cachedAvailability)
  }

  const inFlightRequest = supplierAvailabilityInFlight.get(cacheKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = dataProvider
    .CUSTOM_GET('custom', {
      url: `orgs/${orgId}/ordering/suppliers/${encodeURIComponent(distributor)}/category_availability/`,
    })
    .then((response) => {
      const availabilityResponse = (response?.data || {}) as Record<string, unknown>
      const normalizedAvailability = normalizeAvailabilityResponse(availabilityResponse)
      supplierAvailabilityCache.set(cacheKey, normalizedAvailability)
      return normalizedAvailability
    })
    .catch(() => DEFAULT_BUCKET_AVAILABILITY)
    .finally(() => {
      supplierAvailabilityInFlight.delete(cacheKey)
    })

  supplierAvailabilityInFlight.set(cacheKey, request)
  return request
}

export const useSupplierScopedCategories = (distributor?: HardwareSupplierFilterKeyType) => {
  const dataProvider = useDataProvider()
  const orgId = useSelector(authSelectors.getOrgId)
  const { enabledCategories, getSubtypesForCategory } = useShopCategoryConfig()
  const [bucketAvailability, setBucketAvailability] = useState<ComponentBucketAvailability | null>(null)

  const enabledCategoriesSignature = useMemo(
    () =>
      enabledCategories
        .map((category) => `${category.id}:${category.componentType}:${(category.subtypes || []).join(',')}`)
        .join('|'),
    [enabledCategories]
  )

  useEffect(() => {
    if (!distributor || !orgId) {
      setBucketAvailability(null)
      return
    }

    const cacheKey = `${orgId}:${distributor}`
    const cachedAvailability = supplierAvailabilityCache.get(cacheKey)
    if (cachedAvailability) {
      setBucketAvailability(cachedAvailability)
      return
    }

    setBucketAvailability(null)

    let cancelled = false

    const run = async () => {
      const availability = await getSupplierAvailability(dataProvider, orgId, distributor)

      if (cancelled) return
      setBucketAvailability(availability)
    }

    run()

    return () => {
      cancelled = true
    }
  }, [dataProvider, distributor, enabledCategoriesSignature, orgId])

  return useMemo(() => {
    if (!distributor) return enabledCategories

    if (!bucketAvailability) {
      return []
    }

    return enabledCategories.filter((category) => {
      if (category.componentType === 'module') return bucketAvailability.module
      if (category.componentType === 'inverter') return bucketAvailability.inverter
      if (category.componentType === 'battery') return bucketAvailability.battery
      if (category.componentType === 'other') {
        return categoryHasOtherStock(category, bucketAvailability, getSubtypesForCategory)
      }
      return false
    })
  }, [distributor, enabledCategories, bucketAvailability, getSubtypesForCategory])
}
